import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../../shadcn/components/ui/button";
import { useTranslation } from "../../../context/Internationalization";
import { GET_RESOLVE_USERS } from "../../../graphql/userGraphql";
import useGraphql from "../../../Hooks/useGraphql";
import useLog from "../../../Hooks/useLog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../shadcn/components/ui/card";
import EmptyState from "../../../Components/Common/EmptyState";
import { Item, ItemContent, ItemDescription, ItemGroup, ItemTitle } from "../../../shadcn/components/ui/item";

const ClubAdminsList = ({ idsKey }) => {
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const request = useRef({ axiosGraphQL, errorLog });
  request.current = { axiosGraphQL, errorLog };
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({
    loading: true,
    error: false,
    users: [],
  });

  useEffect(() => {
    let active = true;
    const ids = JSON.parse(idsKey);
    const loadAdmins = async () => {
      setState({ loading: true, error: false, users: [] });
      try {
        const response = await request.current.axiosGraphQL({
          query: GET_RESOLVE_USERS,
          variables: { ids },
        });
        if (!active) return;
        const users = response.data?.data?.resolveUserIds;
        if (!Array.isArray(users))
          throw new Error("Invalid club administrators response");
        const usersById = new Map(
          users.filter(Boolean).map((user) => [user._id, user])
        );
        setState({
          loading: false,
          error: false,
          users: ids.map((id) => usersById.get(id)).filter(Boolean),
        });
      } catch (error) {
        if (!active) return;
        setState({ loading: false, error: true, users: [] });
        request.current.errorLog(
          error,
          "ClubAdminsSection",
          "loadAdmins",
          "GET_RESOLVE_USERS"
        );
      }
    };

    if (ids.length) loadAdmins();
    else setState({ loading: false, error: false, users: [] });
    return () => {
      active = false;
    };
  }, [idsKey, attempt]);

  if (state.loading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        {t("loading")}
      </p>
    );
  }

  if (state.error) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="text-sm text-destructive">
          {t("club_admins_load_failed")}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => setAttempt((value) => value + 1)}
        >
          {t("retry")}
        </Button>
      </div>
    );
  }

  if (!state.users.length) {
    return (
      <EmptyState title={t("no_club_admins")} compact />
    );
  }

  return (
    <ItemGroup aria-label={t("club_admins")} className="gap-2">
      {state.users.map((user) => (
        <Item key={user._id} role="listitem" variant="outline" size="sm">
          <ItemContent className="min-w-0 [overflow-wrap:anywhere]">
            <ItemTitle>
              {[user.firstName, user.lastName]
                .filter(Boolean)
                .join(" ")
                .trim() || t("club_admin")}
            </ItemTitle>
            {user.email && <ItemDescription>{user.email}</ItemDescription>}
            {user.mobile && <ItemDescription>{user.mobile}</ItemDescription>}
          </ItemContent>
        </Item>
      ))}
    </ItemGroup>
  );
};

const ClubAdminsSection = ({ adminIds = [] }) => {
  const { t } = useTranslation();
  const idsKey = JSON.stringify([...new Set((adminIds || []).filter(Boolean))]);

  return (
    <Card size="sm" role="region" aria-labelledby="club-admins-section-title">
      <CardHeader>
        <CardTitle role="heading" aria-level={3} id="club-admins-section-title">{t("club_admins")}</CardTitle>
        <CardDescription>{t("club_admins_description")}</CardDescription>
      </CardHeader>
      <CardContent>
      <ClubAdminsList key={idsKey} idsKey={idsKey} />
    </CardContent>
    </Card>
  );
};

export default ClubAdminsSection;
